import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getIngestToken, regenerateIngestToken, submitJournal, getSubjectiveLogs } from '../services/api';
import toast from 'react-hot-toast';

interface TokenInfo {
  uid: string;
  token: string;
  api_url: string;
}

interface SubjectiveLog {
  date: string;
  raw_text?: string;
  energy_rating?: number;
  mood?: string;
  pain_level?: number;
  notes?: string;
}

export default function ConnectPage() {
  const { currentUser } = useAuth();
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);
  const [logs, setLogs] = useState<SubjectiveLog[]>([]);
  const [journalText, setJournalText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState('');
  const [activeTab, setActiveTab] = useState<'shortcut' | 'journal' | 'logs'>('shortcut');

  const load = useCallback(async () => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const [tokenRes, logsRes] = await Promise.allSettled([
        getIngestToken(token),
        getSubjectiveLogs(token, 14),
      ]);
      if (tokenRes.status === 'fulfilled') setTokenInfo(tokenRes.value);
      if (logsRes.status === 'fulfilled') setLogs(logsRes.value.logs || []);
    } catch {/* ignore */}
  }, [currentUser]);

  useEffect(() => { load(); }, [load]);

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(label);
      setTimeout(() => setCopied(''), 2000);
    });
  };

  const handleRegenerate = async () => {
    if (!currentUser) return;
    if (!window.confirm('This will invalidate your current token. Your iOS Shortcut will stop working until you update it. Continue?')) return;
    try {
      const token = await currentUser.getIdToken();
      const res = await regenerateIngestToken(token);
      setTokenInfo((prev) => prev ? { ...prev, token: res.token } : null);
      toast.success('Token regenerated');
    } catch {
      toast.error('Failed to regenerate token');
    }
  };

  const handleJournalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !journalText.trim()) return;
    setSubmitting(true);
    try {
      const token = await currentUser.getIdToken();
      const res = await submitJournal(token, journalText);
      toast.success(res.message || 'Journal saved');
      setJournalText('');
      load();
    } catch {
      toast.error('Failed to save journal entry');
    } finally {
      setSubmitting(false);
    }
  };

  const shortcutJson = tokenInfo ? JSON.stringify({
    "method": "POST",
    "url": `${tokenInfo.api_url}/api/ingest/watch`,
    "headers": {
      "Content-Type": "application/json",
      "X-VQ-UID": tokenInfo.uid,
      "X-VQ-Token": tokenInfo.token
    },
    "body": {
      "restingHeartRate": "<<Resting Heart Rate>>",
      "heartRateVariabilitySDNN": "<<Heart Rate Variability>>",
      "stepCount": "<<Step Count>>",
      "activeEnergyBurned": "<<Active Energy>>",
      "oxygenSaturation": "<<Blood Oxygen Saturation>>",
      "sleepAnalysis": "<<Sleep Duration>>",
      "date": "<<Current Date>>"
    }
  }, null, 2) : '';

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Connect Devices</h1>
        <p className="text-gray-500 text-sm mt-1">Sync Apple Watch automatically and log health data by text.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
        {[
          { id: 'shortcut', label: '⌚ iOS Shortcut' },
          { id: 'journal', label: '✏️ Health Journal' },
          { id: 'logs', label: '📋 Journal History' },
        ].map(({ id, label }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id as any)}
            className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTab === id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── iOS Shortcut Tab ── */}
      {activeTab === 'shortcut' && (
        <div className="space-y-5">
          {/* Personal Token */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
            <h2 className="font-semibold text-gray-800">Your Personal Ingest Token</h2>
            <p className="text-sm text-gray-500">This token authenticates your iOS Shortcut. Keep it private.</p>

            {tokenInfo ? (
              <>
                <div className="space-y-2">
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">User ID</label>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="flex-1 text-xs bg-gray-50 border border-gray-200 rounded px-3 py-2 text-gray-700 truncate">
                        {tokenInfo.uid}
                      </code>
                      <button
                        onClick={() => handleCopy(tokenInfo.uid, 'uid')}
                        className="text-xs px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded text-gray-600 transition-colors flex-shrink-0"
                      >
                        {copied === 'uid' ? '✓ Copied' : 'Copy'}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Ingest Token</label>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="flex-1 text-xs bg-gray-50 border border-gray-200 rounded px-3 py-2 text-gray-700 truncate font-mono">
                        {tokenInfo.token}
                      </code>
                      <button
                        onClick={() => handleCopy(tokenInfo.token, 'token')}
                        className="text-xs px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded text-gray-600 transition-colors flex-shrink-0"
                      >
                        {copied === 'token' ? '✓ Copied' : 'Copy'}
                      </button>
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleRegenerate}
                  className="text-xs text-red-400 hover:text-red-600 transition-colors"
                >
                  ↺ Regenerate token (invalidates current)
                </button>
              </>
            ) : (
              <div className="text-sm text-gray-400">Loading token...</div>
            )}
          </div>

          {/* iOS Shortcut Setup */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
            <h2 className="font-semibold text-gray-800">iOS Shortcut Setup</h2>
            <p className="text-sm text-gray-500">Sync your Apple Watch data automatically every morning.</p>

            <ol className="space-y-3 text-sm text-gray-700">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-xs font-bold">1</span>
                <span>Open the <strong>Shortcuts</strong> app on your iPhone → tap <strong>+</strong> to create a new shortcut.</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-xs font-bold">2</span>
                <span>Add action: <strong>"Find Health Samples"</strong> — select: Resting Heart Rate, Heart Rate Variability, Step Count, Active Energy, Blood Oxygen, Sleep Duration.</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-xs font-bold">3</span>
                <span>Add action: <strong>"Get Contents of URL"</strong> and configure it as a POST request to:</span>
              </li>
            </ol>

            <div className="relative">
              <code className="block text-xs bg-gray-900 text-emerald-400 rounded-lg p-4 overflow-x-auto whitespace-pre">
                {tokenInfo ? `POST ${tokenInfo.api_url}/api/ingest/watch\n\nHeaders:\n  X-VQ-UID: ${tokenInfo.uid}\n  X-VQ-Token: ${tokenInfo.token}\n  Content-Type: application/json\n\nBody (JSON):\n  restingHeartRate → [Resting Heart Rate value]\n  heartRateVariabilitySDNN → [HRV value]\n  stepCount → [Step Count value]\n  activeEnergyBurned → [Active Energy value]\n  oxygenSaturation → [Blood Oxygen value]\n  sleepAnalysis → [Sleep Duration value]` : 'Loading...'}
              </code>
              {tokenInfo && (
                <button
                  onClick={() => handleCopy(shortcutJson, 'shortcut')}
                  className="absolute top-2 right-2 text-xs px-2 py-1 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded transition-colors"
                >
                  {copied === 'shortcut' ? '✓ Copied' : 'Copy JSON'}
                </button>
              )}
            </div>

            <li className="flex gap-3 list-none">
              <span className="flex-shrink-0 w-6 h-6 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-xs font-bold">4</span>
              <span className="text-sm text-gray-700">Name it <strong>"Sync VitalQuant"</strong> → go to <strong>Automation</strong> → create a new automation to run it <strong>every day at 8:00 AM</strong>.</span>
            </li>
          </div>

          {/* Test endpoint */}
          {tokenInfo && (
            <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Test with curl</p>
              <code className="text-xs text-gray-600 break-all">
                {`curl -X POST ${tokenInfo.api_url}/api/ingest/watch \\
  -H "X-VQ-UID: ${tokenInfo.uid}" \\
  -H "X-VQ-Token: ${tokenInfo.token}" \\
  -H "Content-Type: application/json" \\
  -d '{"restingHeartRate":62,"stepCount":8500}'`}
              </code>
              <button
                onClick={() => handleCopy(
                  `curl -X POST ${tokenInfo.api_url}/api/ingest/watch -H "X-VQ-UID: ${tokenInfo.uid}" -H "X-VQ-Token: ${tokenInfo.token}" -H "Content-Type: application/json" -d '{"restingHeartRate":62,"stepCount":8500}'`,
                  'curl'
                )}
                className="mt-2 text-xs text-emerald-600 hover:text-emerald-800"
              >
                {copied === 'curl' ? '✓ Copied' : 'Copy curl command'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Health Journal Tab ── */}
      {activeTab === 'journal' && (
        <div className="space-y-5">
          <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
            <h2 className="font-semibold text-gray-800">Health Journal</h2>
            <p className="text-sm text-gray-500">
              Type anything — AI extracts the numbers and feelings automatically.
            </p>

            <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3 space-y-1">
              <p className="text-xs font-semibold text-emerald-700">Examples:</p>
              {[
                '"Weight 78.5kg, energy 8/10, feeling really excited about the launch today"',
                '"Slept 6.5 hours, feeling tired, anxiety 6/10, headache since morning"',
                '"BP was 125/82 this morning. Had 9000 steps. Mood calm."',
                '"Feeling like a 4 today, stressed from work, slept badly"',
              ].map((ex, i) => (
                <p key={i} className="text-xs text-emerald-600 cursor-pointer hover:text-emerald-800"
                  onClick={() => setJournalText(ex.replace(/"/g, ''))}>
                  → {ex}
                </p>
              ))}
            </div>

            <form onSubmit={handleJournalSubmit} className="space-y-3">
              <textarea
                value={journalText}
                onChange={(e) => setJournalText(e.target.value)}
                placeholder="How are you feeling? Any vitals to log? Weight, energy level, mood, sleep..."
                rows={5}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
              />
              <button
                type="submit"
                disabled={submitting || !journalText.trim()}
                className="w-full py-2.5 bg-emerald-600 text-white rounded-lg font-semibold text-sm hover:bg-emerald-700 disabled:opacity-50 transition-colors"
              >
                {submitting ? 'Analyzing & saving...' : 'Save Journal Entry'}
              </button>
            </form>
          </div>

          {/* WhatsApp instructions */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-3">
            <h2 className="font-semibold text-gray-800">Via WhatsApp (coming soon)</h2>
            <p className="text-sm text-gray-500">
              Once your WhatsApp number is linked, you can send messages directly to your VitalQuant journal bot. The same AI parser will extract your data and sync it here.
            </p>
            <div className="bg-gray-50 rounded-lg p-3 space-y-1 text-xs text-gray-600">
              <p className="font-medium text-gray-700 mb-1">You'll be able to text things like:</p>
              <p>→ "78kg this morning, feeling great, 8/10"</p>
              <p>→ "Slept 7h, energy low, headache"</p>
              <p>→ "BP 120/78, HRV 52, steps 10k 💪"</p>
            </div>
            <p className="text-xs text-gray-400">To set this up, link your WhatsApp number via your Twilio or Meta Business account and point the webhook to <code className="bg-gray-100 px-1 rounded">/api/ingest/journal</code> with the user's X-VQ-UID and X-VQ-Token in the headers.</p>
          </div>
        </div>
      )}

      {/* ── Journal History Tab ── */}
      {activeTab === 'logs' && (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-800">Journal History (14 days)</h2>
          </div>
          {logs.length === 0 ? (
            <div className="px-6 py-10 text-center text-gray-400 text-sm">
              No journal entries yet. Use the Health Journal tab to add your first entry.
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {logs.map((log, i) => (
                <div key={i} className="px-6 py-4">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="text-sm font-semibold text-gray-700">{log.date}</span>
                    {log.energy_rating && (
                      <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                        Energy {log.energy_rating}/10
                      </span>
                    )}
                    {log.mood && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full capitalize">
                        {log.mood}
                      </span>
                    )}
                    {log.pain_level && (
                      <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                        Pain {log.pain_level}/10
                      </span>
                    )}
                  </div>
                  {log.notes && (
                    <p className="text-sm text-gray-600">{log.notes}</p>
                  )}
                  {log.raw_text && log.raw_text !== log.notes && (
                    <p className="text-xs text-gray-400 mt-1 italic">"{log.raw_text}"</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
